import React, { FunctionComponent, useState } from "react";
import { 
  Typography, 
  Collapse, 
  IconButton, 
  TableCell,
  TableRow
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import styles from "./NewTickets.module.css";
import DynamicTable, { ColumnDef } from "../../components/shared-components/table/DynamicTable";
import { NewListTicket } from "./NewTicketsTypes";
import moment from "moment-timezone";

// Helper for status colors for Delisted Tab
const getDelistedStatusColor = (status: string) => {
  const normalizedStatus = status?.toLowerCase();
  if (normalizedStatus?.includes('delisted')) {
    return { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' }; // Light red bg, dark red text
  } else if (normalizedStatus?.includes('return')) {
    return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' }; // Grey bg, dark grey text
  } else {
    return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' }; // Default Grey
  }
};

const formatTicketDateTime = (dateStr: string) => {
    if (!dateStr) return { date: '-', time: '-' };
    const dt = moment.utc(dateStr).tz('America/Chicago');
    return {
        date: dt.format("MM/DD/YYYY"),
        time: dt.format("h:mm A")
    };
};

const DELISTED_TICKETS_COLUMNS: ColumnDef[] = [
    { id: 'expand', label: '', className: styles.headerCellExpand },
    { id: 'eventName', label: 'EVENT NAME', style: { color: 'rgb(198 198 231)' }, className: styles.headerCell },
    { id: 'section', label: 'SECTION', style: { color: 'rgb(198 198 231)' }, className: styles.headerCell },
    { id: 'row', label: 'ROW', style: { color: 'rgb(198 198 231)' }, className: styles.headerCell },
    { id: 'seat', label: 'SEAT #S', style: { color: 'rgb(198 198 231)' }, className: styles.headerCell },
    { id: 'status', label: 'STATUS', style: { color: 'rgb(198 198 231)', textAlign: 'center' }, className: `${styles.headerCell} ${styles.headerCellCenter}` },
];

interface NewDelistedTicketsProps {
    tickets: NewListTicket[];
}

const NewDelistedTickets: FunctionComponent<NewDelistedTicketsProps> = ({ tickets }) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  
  const toggleRow = (id: string) => {
    setExpandedRows(prev => 
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  return (
    <DynamicTable columns={DELISTED_TICKETS_COLUMNS}>
            {tickets.map((ticket) => {
                const rowId = ticket?.grouped_ticket_data?.[0]?.tp_id;
                const isExpanded = expandedRows.includes(rowId);
                const statusStyle = getDelistedStatusColor(ticket.tp_status);

                // Format date and time
                const { date, time } = formatTicketDateTime(ticket.event_date_time);

                return (
                    <React.Fragment key={rowId}>
                        <TableRow 
                            className={`${styles.tableRow} ${isExpanded ? styles.rowExpanded : ''}`}
                            onClick={() => toggleRow(rowId)}
                        >
                            <TableCell className={styles.bodyCell}>
                                <IconButton size="small">
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                            </TableCell>
                            <TableCell className={styles.bodyCell} component="th" scope="row">
                                <Typography variant="subtitle2" className={styles.eventName}>{ticket.event_name}</Typography>
                                <Typography variant="caption" className={styles.eventDate}>
                                    {date} • {time}
                                </Typography>
                            </TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.section}</TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.row}</TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.seat_range}</TableCell>
                            <TableCell className={styles.bodyCell} align="center">
                                <span className={styles.statusPill} style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border }}>
                                    {ticket.tp_status}
                                </span>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={{ padding: 0, border: 'none' }} colSpan={6}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <div className={styles.expandedContent}>
                                        <div className={styles.expandedHeader}>
                                            <div className={styles.ticketGroupDetails}>
                                                <Typography variant="caption" className={styles.detailsLabel}>Ticket Group Details</Typography>
                                                <div className={styles.detailsInfo}>
                                                    <strong>Section {ticket.section}</strong> <span className={styles.greyDotText}>•</span> <strong>Row {ticket.row}</strong> <span className={styles.greyDotText}>•</span> <span className={styles.greyText}>Seats {ticket.seat_range}</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className={styles.seatsContainer}>
                                            {ticket.grouped_ticket_data.map(seat => {
                                                const seatStatusStyle = getDelistedStatusColor(seat.tp_status);
                                                return (
                                                    <div 
                                                        key={seat.tp_id} 
                                                        className={styles.seatCard}
                                                        style={{ cursor: 'default' }}
                                                    >
                                                        <div className={styles.seatLeft}>
                                                            <Typography variant="body2" className={styles.seatLabel}>Seat {seat.seat_no}</Typography>
                                                            <Typography 
                                                                variant="caption" 
                                                                className={styles.seatStatus}
                                                                style={{ color: seatStatusStyle.color }}
                                                            >
                                                                {seat.tp_status}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                );
            })}
    </DynamicTable>
  );
};

export default NewDelistedTickets;
